import {
  generateFractionalIndexingKeyBetween,
  Store,
} from '@toeverything/infra';

import type { WorkspaceDBService } from '../../db';

export type FolderSubtreeNode = {
  type: (string & {}) | 'folder' | 'doc' | 'tag' | 'collection';
  data: string;
  index: string;
  children: FolderSubtreeNode[];
};

export class FolderStore extends Store {
  constructor(private readonly dbService: WorkspaceDBService) {
    super();
  }

  watchNodeInfo(nodeId: string) {
    return this.dbService.db.folders.get$(nodeId);
  }

  watchNodeChildren(parentId: string | null) {
    return this.dbService.db.folders.find$({
      parentId: parentId,
    });
  }

  watchIsLoading() {
    return this.dbService.db.folders.isLoading$;
  }

  isAncestor(childId: string, ancestorId: string): boolean {
    if (childId === ancestorId) {
      return false;
    }
    const history = new Set<string>([childId]);
    let current: string = childId;
    while (current) {
      const info = this.dbService.db.folders.get(current);
      if (info === null || !info.parentId) {
        return false;
      }
      current = info.parentId;
      if (history.has(current)) {
        return false; // loop detected
      }
      history.add(current);
      if (current === ancestorId) {
        return true;
      }
    }
    return false;
  }

  collectDocIds(folderId: string): string[] {
    const info = this.dbService.db.folders.get(folderId);
    if (info === null || info.type !== 'folder') {
      throw new Error('Folder not found');
    }

    const result = new Set<string>();
    const stack = [folderId];
    while (stack.length > 0) {
      const currentId = stack.pop();
      if (!currentId) {
        continue;
      }
      const children = this.dbService.db.folders.find({ parentId: currentId });
      for (const child of children) {
        if (child.type === 'doc') {
          result.add(child.data);
        } else if (child.type === 'folder') {
          stack.push(child.id);
        }
      }
    }
    return [...result];
  }

  /**
   * Compute a fractional index that places a new child at the end of the
   * given parent's children.
   */
  indexForNewChild(parentId: string | null): string {
    const children = this.dbService.db.folders.find({ parentId });
    const last = children
      .map(child => child.index)
      .filter((index): index is string => !!index)
      .sort((a, b) => (a > b ? 1 : -1))
      .at(-1);
    return generateFractionalIndexingKeyBetween(last ?? null, null);
  }

  /**
   * Recursively duplicate a folder subtree under `targetParentId`.
   *
   * - folder nodes are recreated with the same name and relative order
   * - doc links are remapped through `docIdMap` (docs missing from the map,
   *   e.g. trashed ones, are skipped)
   * - tag/collection links are recreated pointing to the same global target
   *
   * @returns the id of the new root folder copy
   */
  duplicateSubtree(
    sourceFolderId: string,
    targetParentId: string | null,
    docIdMap: ReadonlyMap<string, string>
  ): string {
    const source = this.dbService.db.folders.get(sourceFolderId);
    if (source === null || source.type !== 'folder') {
      throw new Error('Source folder not found');
    }
    if (targetParentId) {
      const parent = this.dbService.db.folders.get(targetParentId);
      if (parent === null || parent.type !== 'folder') {
        throw new Error('Target parent folder not found');
      }
    }

    const rootId = this.dbService.db.folders.create({
      parentId: targetParentId,
      type: 'folder',
      data: source.data,
      index: this.indexForNewChild(targetParentId),
    }).id;

    const copyChildren = (srcParentId: string, newParentId: string) => {
      const children = this.dbService.db.folders.find({
        parentId: srcParentId,
      });
      for (const child of children) {
        if (child.type === 'folder') {
          const newFolderId = this.dbService.db.folders.create({
            parentId: newParentId,
            type: 'folder',
            data: child.data,
            index: child.index,
          }).id;
          copyChildren(child.id, newFolderId);
        } else if (child.type === 'doc') {
          const mapped = docIdMap.get(child.data);
          if (!mapped) {
            continue; // doc was skipped (e.g. trashed)
          }
          this.dbService.db.folders.create({
            parentId: newParentId,
            type: 'doc',
            data: mapped,
            index: child.index,
          });
        } else {
          // tag / collection: link to the same global target
          this.dbService.db.folders.create({
            parentId: newParentId,
            type: child.type,
            data: child.data,
            index: child.index,
          });
        }
      }
    };

    copyChildren(sourceFolderId, rootId);
    return rootId;
  }

  /**
   * Read a folder subtree into a plain, serializable structure. Doc/tag/
   * collection links keep their original target id in `data`.
   */
  exportSubtree(folderId: string): FolderSubtreeNode {
    const info = this.dbService.db.folders.get(folderId);
    if (info === null || info.type !== 'folder') {
      throw new Error('Folder not found');
    }
    const build = (node: {
      type: string;
      data: string;
      index: string;
      id: string;
    }): FolderSubtreeNode => {
      const children = this.dbService.db.folders
        .find({ parentId: node.id })
        .map(child => build(child));
      return {
        type: node.type,
        data: node.data,
        index: node.index,
        children,
      };
    };
    return build(info);
  }

  /**
   * Write a previously exported subtree under `targetParentId` (root when
   * null). Doc links are remapped through `docIdMap`; tag/collection links are
   * skipped (their targets are workspace-specific and may not exist here).
   *
   * @returns the id of the new root folder copy
   */
  importSubtree(
    structure: FolderSubtreeNode,
    targetParentId: string | null,
    docIdMap: ReadonlyMap<string, string>
  ): string {
    if (structure.type !== 'folder') {
      throw new Error('Subtree root must be a folder');
    }
    const rootId = this.dbService.db.folders.create({
      parentId: targetParentId,
      type: 'folder',
      data: structure.data,
      index: this.indexForNewChild(targetParentId),
    }).id;

    const walk = (children: FolderSubtreeNode[], parentId: string) => {
      for (const child of children) {
        if (child.type === 'folder') {
          const newFolderId = this.dbService.db.folders.create({
            parentId,
            type: 'folder',
            data: child.data,
            index: child.index,
          }).id;
          walk(child.children, newFolderId);
        } else if (child.type === 'doc') {
          const mapped = docIdMap.get(child.data);
          if (!mapped) {
            continue;
          }
          this.dbService.db.folders.create({
            parentId,
            type: 'doc',
            data: mapped,
            index: child.index,
          });
        }
        // tag / collection links are workspace-specific, skip on cross-workspace
      }
    };

    walk(structure.children, rootId);
    return rootId;
  }

  createLink(
    parentId: string,
    type: 'doc' | 'tag' | 'collection',
    nodeId: string,
    index: string
  ) {
    const parent = this.dbService.db.folders.get(parentId);
    if (parent === null || parent.type !== 'folder') {
      throw new Error('Parent folder not found');
    }

    this.dbService.db.folders.create({
      parentId,
      type,
      data: nodeId,
      index: index,
    });
  }

  renameNode(nodeId: string, name: string) {
    const node = this.dbService.db.folders.get(nodeId);
    if (node === null) {
      throw new Error('Node not found');
    }
    if (node.type !== 'folder') {
      throw new Error('Cannot rename non-folder node');
    }
    this.dbService.db.folders.update(nodeId, {
      data: name,
    });
  }

  createFolder(parentId: string | null, name: string, index: string) {
    if (parentId) {
      const parent = this.dbService.db.folders.get(parentId);
      if (parent === null || parent.type !== 'folder') {
        throw new Error('Parent folder not found');
      }
    }

    return this.dbService.db.folders.create({
      parentId: parentId,
      type: 'folder',
      data: name,
      index: index,
    }).id;
  }

  removeFolder(folderId: string) {
    const info = this.dbService.db.folders.get(folderId);
    if (info === null || info.type !== 'folder') {
      throw new Error('Folder not found');
    }
    const stack = [info];
    while (stack.length > 0) {
      const current = stack.pop();
      if (!current) {
        continue;
      }
      if (current.type !== 'folder') {
        this.dbService.db.folders.delete(current.id);
      } else {
        const children = this.dbService.db.folders.find({
          parentId: current.id,
        });
        stack.push(...children);
        this.dbService.db.folders.delete(current.id);
      }
    }
  }

  removeLink(linkId: string) {
    const link = this.dbService.db.folders.get(linkId);
    if (link === null || link.type === 'folder') {
      throw new Error('Link not found');
    }
    this.dbService.db.folders.delete(linkId);
  }

  moveNode(nodeId: string, parentId: string | null, index: string) {
    const node = this.dbService.db.folders.get(nodeId);
    if (node === null) {
      throw new Error('Node not found');
    }

    if (parentId) {
      if (nodeId === parentId) {
        throw new Error('Cannot move a node to itself');
      }
      if (this.isAncestor(parentId, nodeId)) {
        throw new Error('Cannot move a node to its descendant');
      }
      const parent = this.dbService.db.folders.get(parentId);
      if (parent === null || parent.type !== 'folder') {
        throw new Error('Parent folder not found');
      }
    } else {
      if (node.type !== 'folder') {
        throw new Error('Root node can only have folders');
      }
    }
    this.dbService.db.folders.update(nodeId, {
      parentId,
      index,
    });
  }
}
